import { Box3, Euler, Matrix4, Mesh, Object3D, Scene, Vector3 } from 'three'

export const FIXED_CLIP_PLANE_SIZE = 40
const FALLBACK_PLANE_SIZE = FIXED_CLIP_PLANE_SIZE
const PLANE_PADDING = 0.2
const MIN_PLANE_SIZE = 2

const BOUNDING_BOX_CORNERS = [
  [0, 0, 0],
  [0, 0, 1],
  [0, 1, 0],
  [0, 1, 1],
  [1, 0, 0],
  [1, 0, 1],
  [1, 1, 0],
  [1, 1, 1],
] as const

export interface ClipPlaneRectangle {
  width: number
  height: number
  centerU: number
  centerV: number
}

export const createFixedClipPlaneRectangle = (): ClipPlaneRectangle => ({
  width: FIXED_CLIP_PLANE_SIZE,
  height: FIXED_CLIP_PLANE_SIZE,
  centerU: 0,
  centerV: 0,
})

export const createClipPlaneRectangle = (
  scene: Scene,
  normal: Vector3,
  planePoint: Vector3,
  sizeFromScene: boolean,
): ClipPlaneRectangle => {
  if (!sizeFromScene) {
    return createFixedClipPlaneRectangle()
  }

  return computeClipPlaneRectangle(collectVisibleScenePoints(scene), normal, planePoint)
}

const isObjectVisible = (object: Object3D): boolean => {
  let current: Object3D | null = object

  while (current) {
    if (!current.visible) return false
    current = current.parent
  }

  return true
}

const shouldExcludeFromClipBounds = (object: Object3D): boolean => {
  if (object.userData?.clipController === true) return true
  if (object.name.startsWith('clip-controller')) return true

  const excludedTypes = new Set([
    'GridHelper',
    'AxesHelper',
    'Line',
    'LineSegments',
    'LineLoop',
    'Points',
    'Sprite',
    'Camera',
    'Light',
    'AmbientLight',
    'DirectionalLight',
    'PointLight',
    'SpotLight',
    'HemisphereLight',
  ])

  if (excludedTypes.has(object.type)) return true

  return false
}

const expandBoxCorners = (box: Box3, points: Vector3[]) => {
  const { min, max } = box

  BOUNDING_BOX_CORNERS.forEach(([xi, yi, zi]) => {
    points.push(new Vector3(xi ? max.x : min.x, yi ? max.y : min.y, zi ? max.z : min.z))
  })
}

/**
 * World-space axis-aligned bounding box of all visible meshes in the scene.
 */
export const getVisibleSceneBoundingBox = (scene: Scene): Box3 => {
  const sceneBox = new Box3()
  const meshBox = new Box3()

  scene.traverse(object => {
    if (shouldExcludeFromClipBounds(object)) return
    if (!(object instanceof Mesh)) return
    if (!isObjectVisible(object)) return
    if (!object.geometry) return

    meshBox.setFromObject(object, true)

    if (!meshBox.isEmpty()) {
      sceneBox.union(meshBox)
    }
  })

  return sceneBox
}

/** Eight corners of the visible scene bounding box (for projecting onto a clip plane). */
export const collectVisibleScenePoints = (scene: Scene): Vector3[] => {
  const sceneBox = getVisibleSceneBoundingBox(scene)

  if (sceneBox.isEmpty()) return []

  const points: Vector3[] = []
  expandBoxCorners(sceneBox, points)

  return points
}

export const makePlaneBasis = (normal: Vector3): { u: Vector3; v: Vector3 } => {
  const normalized = normal.clone().normalize()
  const reference = Math.abs(normalized.z) < 0.9 ? new Vector3(0, 0, 1) : new Vector3(0, 1, 0)
  const u = new Vector3().crossVectors(reference, normalized).normalize()
  const v = new Vector3().crossVectors(normalized, u).normalize()

  return { u, v }
}

/**
 * Rotation for a PlaneGeometry mesh (local XY, +Z normal) so local X/Y align with the
 * same u/v basis used by {@link computeClipPlaneRectangle}.
 */
export const makeClipPlaneRotation = (normal: Vector3): Euler => {
  const normalized = normal.clone().normalize()
  const { u, v } = makePlaneBasis(normalized)

  return new Euler().setFromRotationMatrix(new Matrix4().makeBasis(u, v, normalized))
}

export const computeClipPlaneRectangle = (
  points: Vector3[],
  normal: Vector3,
  planePoint: Vector3,
): ClipPlaneRectangle => {
  if (points.length === 0) {
    return {
      width: FALLBACK_PLANE_SIZE,
      height: FALLBACK_PLANE_SIZE,
      centerU: 0,
      centerV: 0,
    }
  }

  const normalizedNormal = normal.clone().normalize()
  const { u, v } = makePlaneBasis(normalizedNormal)

  let minU = Infinity
  let maxU = -Infinity
  let minV = Infinity
  let maxV = -Infinity

  points.forEach(point => {
    const distanceToPlane = point.clone().sub(planePoint).dot(normalizedNormal)
    const projected = point.clone().sub(normalizedNormal.clone().multiplyScalar(distanceToPlane))
    const relative = projected.sub(planePoint)
    const uCoord = relative.dot(u)
    const vCoord = relative.dot(v)

    minU = Math.min(minU, uCoord)
    maxU = Math.max(maxU, uCoord)
    minV = Math.min(minV, vCoord)
    maxV = Math.max(maxV, vCoord)
  })

  if (!Number.isFinite(minU)) {
    return {
      width: FALLBACK_PLANE_SIZE,
      height: FALLBACK_PLANE_SIZE,
      centerU: 0,
      centerV: 0,
    }
  }

  return {
    width: Math.max(maxU - minU + PLANE_PADDING * 2, MIN_PLANE_SIZE),
    height: Math.max(maxV - minV + PLANE_PADDING * 2, MIN_PLANE_SIZE),
    centerU: (minU + maxU) / 2,
    centerV: (minV + maxV) / 2,
  }
}

export const getClipPlaneCenterOnPlane = (
  planePoint: Vector3,
  normal: Vector3,
  rectangle: ClipPlaneRectangle,
): Vector3 => {
  const { u, v } = makePlaneBasis(normal)

  return planePoint
    .clone()
    .add(u.multiplyScalar(rectangle.centerU))
    .add(v.multiplyScalar(rectangle.centerV))
}

export const makeClipPlaneOutlinePoints = (width: number, height: number): Vector3[] => {
  const halfWidth = width / 2
  const halfHeight = height / 2

  return [
    new Vector3(-halfWidth, -halfHeight, 0),
    new Vector3(halfWidth, -halfHeight, 0),
    new Vector3(halfWidth, halfHeight, 0),
    new Vector3(-halfWidth, halfHeight, 0),
    new Vector3(-halfWidth, -halfHeight, 0),
  ]
}
